import React, { useEffect, useCallback, useState, ReactElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { ApplicationItem } from '../../services/applicationService';
import { logger } from '../../utils/logger';
import ApplicationActionPanel from './ApplicationActionPanel';
import './ApplicationDetailsModal.css';

interface ApplicationDetailsModalProps {
  application: ApplicationItem;
  onClose: () => void;
  renderStatusBadge: (status: string) => ReactElement;
  formatDate: (dateString: string) => string;
  processingId: string | null;
  rejectionReason: string;
  onReasonChange: (reason: string) => void;
  onApprove: (application: ApplicationItem) => void;
  onReject: (application: ApplicationItem) => void;
  useStandardMessage: boolean;
  onUseStandardMessageChange: (useStandard: boolean) => void;
  standardMessages: string[];
  selectedStandardMessage: string;
  onStandardMessageSelect: (message: string) => void;
}

const ApplicationDetailsModal: React.FC<ApplicationDetailsModalProps> = ({
  application,
  onClose,
  renderStatusBadge,
  formatDate,
  processingId,
  rejectionReason,
  onReasonChange,
  onApprove,
  onReject,
  useStandardMessage,
  onUseStandardMessageChange,
  standardMessages,
  selectedStandardMessage,
  onStandardMessageSelect
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  
  // Handle ESC key press
  const handleEscKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Detect if we're on mobile
  const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

  // Animation effect on open
  useEffect(() => {
    setIsVisible(true);
    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [handleEscKey]);

  // Stop click propagation
  const handleModalContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      className="adm-modal-backdrop" 
      tabIndex={-1} 
      aria-modal="true" 
      role="dialog"
      onClick={onClose}
    >
      <div 
        className={`adm-modal ${isVisible ? 'adm-visible' : ''} ${isMobile ? 'adm-mobile-fullscreen' : ''}`}
        tabIndex={-1}
        role="document"
        onClick={handleModalContentClick}
      >
        <div className="adm-modal-header">
          <h2>Application Details</h2>
          <button
            className="adm-close-button"
            aria-label="Close"
            onClick={onClose}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="adm-modal-body">
          <div className="adm-detail-row">
            <span className="adm-detail-label">Status:</span>
            <span className="adm-detail-value">{renderStatusBadge(application.status)}</span>
          </div>

          <div className="adm-detail-row">
            <span className="adm-detail-label">Submitted:</span>
            <span className="adm-detail-value">{formatDate(application.submittedAt)}</span>
          </div>

          <div className="adm-detail-row">
            <span className="adm-detail-label">Name/Alias:</span>
            <span className="adm-detail-value">{application.name}</span>
          </div>

          <div className="adm-detail-row">
            <span className="adm-detail-label">Email:</span>
            <span className="adm-detail-value">{application.email}</span>
          </div>

          <div className="adm-detail-row">
            <span className="adm-detail-label">Attendee Type:</span>
            <span className="adm-detail-value">
              {application.attendeeType === 'single' ? 'Single Attendee' : 'Couple'}
            </span>
          </div>

          {application.attendeeType === 'couple' && application.partnerAlias && (
            <div className="adm-detail-row">
              <span className="adm-detail-label">Partner Alias:</span>
              <span className="adm-detail-value">{application.partnerAlias}</span>
            </div>
          )}

          {application.photoUrl && (
            <div className="adm-detail-row adm-photo-row">
              <span className="adm-detail-label">Photo:</span>
              <div className="adm-detail-value">
                <img 
                  src={application.photoUrl} 
                  alt="Applicant" 
                  className="adm-applicant-photo" 
                  onClick={() => window.open(application.photoUrl, '_blank')}
                />
              </div>
            </div>
          )}

          <div className="adm-detail-section">
            <h3>Understanding of Consent</h3>
            <p className="adm-consent-text">{application.understandingConsent}</p>
          </div>

          {application.rejectionReason && application.status === 'rejected' && (
            <div className="adm-detail-section adm-rejection-section">
              <h3>Rejection Reason</h3>
              <p>{application.rejectionReason}</p>
            </div>
          )}
          
          {application.status === 'approved' && application.qrCodeUrl && (
            <div className="adm-qr-section">
              <h3>Entry QR Code</h3>
              <div className="adm-qr-container">
                <img 
                  src={application.qrCodeUrl} 
                  alt="Entry QR Code" 
                  className="adm-qr-code"
                  onClick={() => {
                    // Open in modal or new window
                    logger.info('🔍 Opening QR code in full size');
                    window.open(application.qrCodeUrl, '_blank');
                  }}
                />
              </div>
              <p className="adm-qr-note">
                This QR code is unique to the applicant and must be presented at entry.
                Click the QR code to open in a new tab.
              </p>
            </div>
          )}
        </div>
        
        {/* Action Panel */}
        <ApplicationActionPanel
          application={application}
          processingId={processingId}
          rejectionReason={rejectionReason}
          onReasonChange={onReasonChange}
          onApprove={onApprove}
          onReject={onReject}
          useStandardMessage={useStandardMessage}
          onUseStandardMessageChange={onUseStandardMessageChange}
          standardMessages={standardMessages}
          selectedStandardMessage={selectedStandardMessage}
          onStandardMessageSelect={onStandardMessageSelect}
        />
      </div>
    </div>
  );
};

export default ApplicationDetailsModal;
